import { type ColumnVersionSnapshot } from "../../../hooks/useColumnHistory";
import { formatRelativeTime } from "../../../lib/utils";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { cn } from "../../../lib/utils";

export interface VersionRailProps {
  history: ColumnVersionSnapshot[];
  selectedVersion: number | null;
  onSelectVersion: (version: number) => void;
  onRestore: (snapshot: ColumnVersionSnapshot) => void;
  resolveChangedBy?: (changedBy: string) => string;
  /** Custom display label per version (defaults to "v{n}"). Lets callers slot
   * pseudo-entries — e.g. the revise wizard's "Saved" baseline — into the rail. */
  versionLabel?: (version: number) => string;
}

function resolveAuthor(
  changedBy: string,
  resolver: ((id: string) => string) | undefined,
): string {
  if (changedBy === "system") return "System";
  const resolved = resolver?.(changedBy);
  return resolved || changedBy;
}

export function VersionRail({
  history,
  selectedVersion,
  onSelectVersion,
  onRestore,
  resolveChangedBy,
  versionLabel = (version) => `v${version}`,
}: VersionRailProps) {
  return (
    <div className="flex flex-col gap-1 w-56 shrink-0">
      {/* Current row — non-selectable, no restore */}
      <div className="flex items-center justify-between px-3 py-2 rounded-md border border-stone-200 bg-stone-50">
        <span className="text-sm font-medium text-stone-700">Current</span>
        <Badge variant="secondary">Current</Badge>
      </div>

      {/* Prior version rows — newest-first (already ordered by caller) */}
      {history.map((snapshot) => {
        const isSelected = snapshot.version === selectedVersion;
        const author = resolveAuthor(snapshot.changedBy, resolveChangedBy);
        const relativeTime = formatRelativeTime(snapshot.createdAt);

        return (
          <div
            key={snapshot.id}
            role="button"
            aria-pressed={isSelected}
            tabIndex={0}
            onClick={() => onSelectVersion(snapshot.version)}
            onKeyDown={(e) => {
              // Only the row itself selects — ignore key events bubbling up
              // from the focused Restore button (Enter/Space there should
              // restore, not also change the selected diff).
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelectVersion(snapshot.version);
              }
            }}
            className={cn(
              "flex flex-col gap-1.5 px-3 py-2 rounded-md border cursor-pointer transition-colors",
              isSelected
                ? "border-stone-900 bg-stone-100"
                : "border-stone-200 bg-white hover:bg-stone-50",
            )}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-stone-900">
                {versionLabel(snapshot.version)}
              </span>
              <span className="text-xs text-stone-500">{relativeTime}</span>
            </div>
            <span className="text-xs text-stone-600">{author}</span>
            <div className="w-fit">
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  // Restore on its own — don't also select the row's diff.
                  e.stopPropagation();
                  onRestore(snapshot);
                }}
              >
                Restore {versionLabel(snapshot.version)}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
